import { Fragment } from "react";
import {
  FORMULA_ERROR_STRINGS,
  type FormulaErrorStrings,
  FormulaView,
  formulaErrorMessage,
} from "@/components/tools/chemistry/formula-view";
import { t } from "@/lib/i18n/interpolate";
import type {
  EquationError,
  OrientedEquation,
  Species,
  Term,
} from "@/lib/tool-kit/chem-reactions/balance";

/**
 * Shared pieces of the reaction widgets: an equation drawn with real subscripts and
 * superscripts, and the English messages for an equation that will not parse.
 */

export const EQUATION_ERROR_STRINGS = {
  empty: "Type an equation, such as H2 + O2 -> H2O.",
  noArrow:
    "Put an arrow between the reactants and the products: ->, → or =. Leave a space before it after an ion such as Cl-.",
  manyArrows:
    "Use one arrow. Write a multi-step reaction as separate equations.",
  emptySide: "Both sides of the arrow need at least one species.",
  emptySpecies:
    "A + sign has nothing beside it. Remove it or add the missing species.",
  tooMany:
    "That is more than 20 species or 30 elements, which is beyond this tool.",
  badSpecies: "“{species}” cannot be read. {reason}",
  formulaErrors: FORMULA_ERROR_STRINGS,
} as const;

export type EquationErrorStrings = {
  [K in keyof typeof EQUATION_ERROR_STRINGS]: K extends "formulaErrors"
    ? FormulaErrorStrings
    : string;
};

export function equationErrorMessage(
  strings: EquationErrorStrings,
  error: EquationError
): string {
  if (error.code === "badSpecies") {
    return t(strings.badSpecies, {
      species: error.token,
      reason: error.formulaError
        ? formulaErrorMessage(strings.formulaErrors, error.formulaError)
        : "",
    }).trim();
  }
  return strings[error.code];
}

export function SpeciesView({ species }: { species: Species }) {
  if (species.formula === null) {
    return <span>e⁻</span>;
  }
  return <FormulaView formula={species.formula} />;
}

function Terms({ terms }: { terms: readonly Term[] }) {
  return (
    <>
      {terms.map(({ coefficient, species }, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: positional terms of one equation
        <Fragment key={index}>
          {index > 0 ? " + " : null}
          {coefficient === 1 ? null : `${coefficient} `}
          <SpeciesView species={species} />
        </Fragment>
      ))}
    </>
  );
}

/** `2 H₂ + O₂ → 2 H₂O` with real sub- and superscripts. */
export function EquationView({
  oriented,
  arrow = "→",
}: {
  oriented: Pick<OrientedEquation, "left" | "right">;
  arrow?: string;
}) {
  return (
    <span className="break-words">
      <Terms terms={oriented.left} /> {arrow} <Terms terms={oriented.right} />
    </span>
  );
}

function termsText(terms: readonly Term[]): string {
  return terms
    .map(({ coefficient, species }) =>
      coefficient === 1 ? species.label : `${coefficient} ${species.label}`
    )
    .join(" + ");
}

/** The equation as plain Unicode text, for copying. */
export function equationText(
  oriented: Pick<OrientedEquation, "left" | "right">
): string {
  return `${termsText(oriented.left)} → ${termsText(oriented.right)}`;
}
